import React, { useCallback, useRef, useState } from 'react';
import { ListItemIcon, ListItemText, Menu, MenuItem, useMediaQuery } from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import PhotoLibraryIcon from '@mui/icons-material/PhotoLibrary';

type UseMobileImagePickerOptions = {
  onFileSelected: (file: File) => void;
  /** Appel� juste avant l'ouverture cam�ra/galerie (persistance brouillon Android). */
  onBeforeOpen?: () => void;
  accept?: string;
  disabled?: boolean;
};

/**
 * Sur Android (ex. Xiaomi/Chrome), accept="image/*" seul ouvre souvent la galerie sans appareil photo.
 * Sur �cran tactile : menu � Prendre une photo � (capture) / � Galerie �.
 */
export function useMobileImagePicker({
  onFileSelected,
  onBeforeOpen,
  accept = 'image/*,.heic,.heif',
  disabled = false,
}: UseMobileImagePickerOptions) {
  const galleryRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const isCoarsePointer = useMediaQuery('(pointer: coarse)');
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        onFileSelected(file);
      }
      event.target.value = '';
    },
    [onFileSelected],
  );

  const openGallery = useCallback(() => {
    setMenuAnchor(null);
    if (!disabled) {
      onBeforeOpen?.();
      galleryRef.current?.click();
    }
  }, [disabled, onBeforeOpen]);

  const openCamera = useCallback(() => {
    setMenuAnchor(null);
    if (!disabled) {
      onBeforeOpen?.();
      cameraRef.current?.click();
    }
  }, [disabled, onBeforeOpen]);

  const openPicker = useCallback(
    (anchorEl?: HTMLElement | null) => {
      if (disabled) return;
      if (isCoarsePointer) {
        setMenuAnchor(anchorEl ?? null);
        if (!anchorEl) {
          openCamera();
        }
      } else {
        onBeforeOpen?.();
        galleryRef.current?.click();
      }
    },
    [disabled, isCoarsePointer, onBeforeOpen, openCamera],
  );

  const hiddenInputs = (
    <>
      <input
        ref={galleryRef}
        type="file"
        accept={accept}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
      <input
        ref={cameraRef}
        type="file"
        accept={accept}
        capture="environment"
        onChange={handleChange}
        style={{ display: 'none' }}
      />
    </>
  );

  const sourceMenu = (
    <Menu
      anchorEl={menuAnchor}
      open={Boolean(menuAnchor)}
      onClose={() => setMenuAnchor(null)}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      transformOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <MenuItem onClick={openCamera}>
        <ListItemIcon>
          <PhotoCameraIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Prendre une photo" />
      </MenuItem>
      <MenuItem onClick={openGallery}>
        <ListItemIcon>
          <PhotoLibraryIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Choisir dans la galerie" />
      </MenuItem>
    </Menu>
  );

  return { openPicker, hiddenInputs, sourceMenu };
}
